import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Search, LogOut, Loader2, Trash2, CheckCircle2, RotateCcw,
  Mail, MailX, Inbox, ChevronDown, ChevronUp,
} from 'lucide-react';
import { companyInfo } from '../mock';

export const AdminDashboard = () => {
  const { user, logout, authAxios } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const api = authAxios();

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/contact');
      setInquiries(data);
    } catch (e) {
      toast.error('Failed to load inquiries.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInquiries(); }, [fetchInquiries]);

  const toggleStatus = async (inq) => {
    const next = inq.status === 'handled' ? 'new' : 'handled';
    try {
      await api.patch(`/contact/${inq.id}/status`, { status: next });
      setInquiries((prev) => prev.map((i) => (i.id === inq.id ? { ...i, status: next } : i)));
      toast.success(`Marked as ${next === 'handled' ? 'Handled' : 'New'}.`);
    } catch (e) {
      toast.error('Failed to update status.');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/contact/${deleteTarget.id}`);
      setInquiries((prev) => prev.filter((i) => i.id !== deleteTarget.id));
      toast.success('Inquiry deleted.');
    } catch (e) {
      toast.error('Failed to delete inquiry.');
    } finally {
      setDeleteTarget(null);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return inquiries.filter((i) => {
      if (statusFilter !== 'all' && i.status !== statusFilter) return false;
      if (!q) return true;
      return (
        (i.name || '').toLowerCase().includes(q) ||
        (i.email || '').toLowerCase().includes(q) ||
        (i.course || '').toLowerCase().includes(q) ||
        (i.company || '').toLowerCase().includes(q)
      );
    });
  }, [inquiries, query, statusFilter]);

  const stats = useMemo(() => ({
    total: inquiries.length,
    newCount: inquiries.filter((i) => i.status === 'new').length,
    handled: inquiries.filter((i) => i.status === 'handled').length,
  }), [inquiries]);

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-slate-900 text-white">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img src={companyInfo.logoUrl} alt="PWCA" className="h-10 w-10 object-contain" />
            <div>
              <h1 className="font-black text-lg leading-tight">PWCA Admin</h1>
              <p className="text-orange-300 text-xs">{user?.email}</p>
            </div>
          </div>
          <Button onClick={logout} data-testid="admin-logout-button" variant="outline" className="border-orange-400 text-orange-300 hover:bg-orange-500/10 bg-transparent">
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl shadow-sm p-5 border-l-4 border-slate-400">
            <p className="text-gray-500 text-sm">Total Inquiries</p>
            <p className="text-3xl font-black text-slate-900" data-testid="stat-total">{stats.total}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm p-5 border-l-4 border-orange-500">
            <p className="text-gray-500 text-sm">New</p>
            <p className="text-3xl font-black text-orange-600" data-testid="stat-new">{stats.newCount}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm p-5 border-l-4 border-green-500">
            <p className="text-gray-500 text-sm">Handled</p>
            <p className="text-3xl font-black text-green-600" data-testid="stat-handled">{stats.handled}</p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              data-testid="admin-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, email, course, or company..."
              className="pl-10 rounded-xl border-2"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'new', 'handled'].map((s) => (
              <Button
                key={s}
                data-testid={`filter-${s}`}
                onClick={() => setStatusFilter(s)}
                variant={statusFilter === s ? 'default' : 'outline'}
                className={statusFilter === s ? 'bg-orange-500 hover:bg-orange-600 capitalize' : 'capitalize'}
              >
                {s}
              </Button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading inquiries...
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400" data-testid="admin-empty-state">
              <Inbox className="h-12 w-12 mb-3" />
              <p className="font-medium">No inquiries found.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-100">
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Course</TableHead>
                  <TableHead>Received</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((inq) => (
                  <React.Fragment key={inq.id}>
                    <TableRow data-testid={`inquiry-row-${inq.id}`} className="cursor-pointer hover:bg-orange-50" onClick={() => setExpandedId(expandedId === inq.id ? null : inq.id)}>
                      <TableCell>{expandedId === inq.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</TableCell>
                      <TableCell>
                        <div className="font-semibold text-slate-900">{inq.name}</div>
                        <div className="text-xs text-gray-500">{inq.email}</div>
                      </TableCell>
                      <TableCell className="max-w-[220px] truncate text-sm">{inq.course || 'General'}</TableCell>
                      <TableCell className="text-sm text-gray-600 whitespace-nowrap">{fmtDate(inq.created_at)}</TableCell>
                      <TableCell>
                        {inq.email_status === 'sent' ? (
                          <span className="inline-flex items-center text-green-600 text-xs font-medium"><Mail className="h-3.5 w-3.5 mr-1" />Sent</span>
                        ) : (
                          <span className="inline-flex items-center text-red-500 text-xs font-medium"><MailX className="h-3.5 w-3.5 mr-1" />Failed</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold ${inq.status === 'handled' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                          {inq.status === 'handled' ? 'Handled' : 'New'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <Button data-testid={`toggle-status-${inq.id}`} size="sm" variant="ghost" onClick={() => toggleStatus(inq)} title={inq.status === 'handled' ? 'Mark as New' : 'Mark as Handled'}>
                          {inq.status === 'handled' ? <RotateCcw className="h-4 w-4 text-orange-500" /> : <CheckCircle2 className="h-4 w-4 text-green-600" />}
                        </Button>
                        <Button data-testid={`delete-${inq.id}`} size="sm" variant="ghost" onClick={() => setDeleteTarget(inq)} title="Delete">
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    {expandedId === inq.id && (
                      <TableRow className="bg-slate-50">
                        <TableCell colSpan={7}>
                          <div className="p-4 grid md:grid-cols-2 gap-4 text-sm">
                            <div><span className="font-semibold text-gray-700">Phone:</span> {inq.phone || '-'}</div>
                            <div><span className="font-semibold text-gray-700">Company:</span> {inq.company || '-'}</div>
                            <div><span className="font-semibold text-gray-700">Preferred Format:</span> {inq.trainingFormat || '-'}</div>
                            <div><span className="font-semibold text-gray-700">Email:</span> {inq.email}</div>
                            <div className="md:col-span-2">
                              <span className="font-semibold text-gray-700">Message:</span>
                              <p className="mt-1 p-3 bg-white rounded-lg border whitespace-pre-wrap">{inq.message || '-'}</p>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this inquiry?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the inquiry from <strong>{deleteTarget?.name}</strong>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-button" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
